// Reference-photo grid for the upload app's FilePond uploaders on product pages (snippets/ref-upload-gallery.liquid).
// FilePond's own list (big previews, file names on edit) is hidden once a uploader has files; instead each uploader
// gets a compact grid right under it. One grid per uploader, so every shirt in a pack keeps its own photos.
// - Tap a photo: opens the cart's swipe viewer on that uploader's photos (window.cpOpenImageViewer, s-cart-addon.js).
// - Remove: the x asks first ("Remove" / "Keep", auto-cancels), then presses the app's own remove control.
// Works with or without FilePond's global API: without it, photos and states are read from FilePond's markup.

const galleryTemplate = document.getElementById('ref-upload-gallery-template');
const itemTemplate = document.getElementById('ref-upload-item-template');
const CONFIRM_TIMEOUT = 5000;
const blobUrls = new Map();
const pending = new Set();
let frame = 0;

const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replace(`[${key}]`, String(value)), template || '');
const isImageUrl = (value) => typeof value === 'string' && /^(https?:|blob:|data:image)/.test(value.trim());

// FilePond.FileStatus numbers: 1 init, 3 processing, 7 loading, 9 queued are "busy"; 6, 8, 10 are errors.
const stateFromStatus = (status) => ([1, 3, 7, 9].includes(status) ? 'busy' : [6, 8, 10].includes(status) ? 'error' : 'ready');
const stateFromAttr = (attr = '') => (attr.includes('error') ? 'error' : /loading|processing(?!-complete)|busy|idle-queued/.test(attr) && !attr.includes('complete') ? 'busy' : 'ready');

function urlForFile(file) {
  if (isImageUrl(file.source)) return file.source.trim();
  const fromServer = urlFromValue(file.serverId);
  if (fromServer) return fromServer;
  if (pickedFiles.has(file.filename)) return pickedFiles.get(file.filename);
  const blob = file.file;
  if (blob instanceof Blob && (!blob.type || blob.type.startsWith('image/'))) {
    if (!blobUrls.has(file.id)) blobUrls.set(file.id, URL.createObjectURL(blob));
    return blobUrls.get(file.id);
  }
  return '';
}

function urlFromMarkup(element) {
  const hidden = [...element.querySelectorAll('input')].map((input) => urlFromValue(input.value)).find(Boolean);
  if (hidden) return hidden;
  const image = element.querySelector('img[src]');
  if (image && isImageUrl(image.src)) return image.src;
  const canvas = element.querySelector('.filepond--image-preview canvas, canvas');
  if (canvas instanceof HTMLCanvasElement && canvas.width > 1) {
    try { return canvas.toDataURL('image/jpeg', 0.85); } catch {}
  }
  return '';
}

// Photos the shopper picked, captured from the file input / drop before FilePond takes them, keyed by name + size.
const pickedFiles = new Map();
const rememberFiles = (files) => {
  [...(files || [])].forEach((file) => {
    if (!(file instanceof File) || (file.type && !file.type.startsWith('image/'))) return;
    const url = URL.createObjectURL(file);
    pickedFiles.set(file.name, url);
  });
};
document.addEventListener('change', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.type === 'file' && input.closest('.filepond--root')) rememberFiles(input.files);
}, true);
document.addEventListener('drop', (event) => {
  if (event.target instanceof Element && event.target.closest('.filepond--root')) rememberFiles(event.dataTransfer?.files);
}, true);
document.addEventListener('paste', (event) => rememberFiles(event.clipboardData?.files), true);

// Stored values can be a plain URL or JSON (e.g. {"url": "..."}); pull an image link out of either.
function urlFromValue(value) {
  const text = String(value || '').trim();
  if (isImageUrl(text)) return text;
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      const found = JSON.stringify(JSON.parse(text)).match(/https?:\\?\/\\?\/[^"\s]+/);
      if (found) return found[0].replace(/\\\//g, '/');
    } catch {}
  }
  const inText = text.match(/https?:\/\/[^\s"',]+/);
  return inText ? inText[0] : '';
}

// Editing from the cart: the app reloads saved photos by file name only (no preview). The cart already holds each
// photo's link, so links from the cart's line details are matched to those names. Fetched once, only when needed.
let cartPhotos = null;
let cartRequested = false;
const photoKey = (value) => {
  let name = String(value || '').split('?')[0].split('/').pop() || '';
  try { name = decodeURIComponent(name); } catch {}
  return name.trim().toLowerCase();
};
const withoutExtension = (name) => name.replace(/\.[a-z0-9]{2,5}$/, '');

function loadCartPhotos() {
  if (cartRequested) return;
  cartRequested = true;
  const root = window.Shopify?.routes?.root || '/';
  fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => {
      cartPhotos = new Map();
      (cart.items || []).forEach((item) => {
        Object.values(item.properties || {}).forEach((value) => {
          const url = urlFromValue(value);
          if (url) cartPhotos.set(photoKey(url), url);
        });
      });
      editPhotoGroups = groupsForEditedLine(cart);
      schedule();
    })
    .catch(() => {});
}

// Backup when names don't match: the line the shopper tapped Edit on (stored by s-cart-addon.js as cp:editing) has
// its photos grouped per uploader ("Reference Images - Shirt 2_3" → "... - Shirt 2"), in the same order as the
// uploaders on the page. Only that line is used, so a fresh upload never borrows photos from another cart item.
let editPhotoGroups = [];
function groupsForEditedLine(cart) {
  let editing = null;
  try { editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null'); } catch {}
  const productId = Number(document.querySelector('[data-product-form][data-product-id]')?.dataset.productId || 0);
  const line = (cart.items || []).find((item) => editing?.key && item.key === editing.key && (!productId || item.product_id === productId));
  if (!line) return [];
  const groups = new Map();
  Object.entries(line.properties || {}).forEach(([key, value]) => {
    if (key.startsWith('_') || /thumbnail_/i.test(key)) return;
    const url = urlFromValue(value);
    if (!url) return;
    const group = key.trim().replace(/_\d+$/, '').trim();
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(url);
  });
  return [...groups.values()];
}

function editPhotoFor(root, index) {
  if (!editPhotoGroups.length) return '';
  const roots = [...document.querySelectorAll('.filepond--root')].filter((el) => el.querySelector('.filepond--item'));
  const group = editPhotoGroups[Math.max(0, roots.indexOf(root))] || [];
  return group[index] || '';
}

function cartPhotoFor(name) {
  if (!cartPhotos) {
    loadCartPhotos();
    return '';
  }
  const key = photoKey(name);
  if (!key) return '';
  if (cartPhotos.has(key)) return cartPhotos.get(key);
  const bare = withoutExtension(key);
  for (const [savedKey, url] of cartPhotos) {
    const savedBare = withoutExtension(savedKey);
    if (savedKey.endsWith(key) || key.endsWith(savedKey) || (bare && (savedBare.endsWith(bare) || bare.endsWith(savedBare)))) return url;
  }
  return '';
}

// Each photo's image is locked to that photo the first time it's found (by FilePond file id / list element), so
// removing one never shifts the others' images. Order-based guesses (the app's saved values, the edited cart
// line) are only trusted for the original set, before anything was added or removed.
const lockedUrls = new Map();
let refSerial = 0;

function stableId(element) {
  if (element.id) return element.id.replace(/^filepond--item-/, '');
  if (!element.dataset.cpRefId) element.dataset.cpRefId = `cp-ref-${++refSerial}`;
  return element.dataset.cpRefId;
}

function resolveUrl(root, id, direct, name, index, orderValue) {
  if (lockedUrls.has(id)) return lockedUrls.get(id);
  const orderTrusted = root.dataset.cpRefChanged !== 'true';
  const url = direct || pickedFiles.get(name) || cartPhotoFor(name) || (orderTrusted ? orderValue || editPhotoFor(root, index) : '');
  if (url) lockedUrls.set(id, url);
  return url;
}

function readItems(root) {
  const pond = window.FilePond?.find?.(root);
  if (pond && typeof pond.getFiles === 'function') {
    return pond.getFiles().map((file, index) => {
      const id = String(file.id);
      return { id, url: resolveUrl(root, id, urlForFile(file), file.filename, index, ''), state: stateFromStatus(file.status) };
    });
  }
  // Without the API: FilePond keeps one hidden value per file (same order as the list) in .filepond--data.
  const dataValues = [...root.querySelectorAll('.filepond--data input')].map((input) => urlFromValue(input.value));
  return [...root.querySelectorAll('.filepond--item')].map((element, index) => {
    const id = stableId(element);
    const name = element.querySelector('.filepond--file-info-main')?.textContent?.trim() || '';
    return {
      id,
      url: resolveUrl(root, id, urlFromMarkup(element), name, index, dataValues[index]),
      state: stateFromAttr(element.getAttribute('data-filepond-item-state') || ''),
    };
  });
}

// The field name ("properties[Reference Images - Shirt 2]") makes a clear viewer heading for packs.
// Title for a uploader's photos ("Shirt 2 reference images"). Read from the label the app shows above the uploader
// (or a field name that mentions the shirt); app-internal names like "_cl-upload…" are never shown. With several
// uploaders and no shirt in the label, they're numbered in page order (Shirt 1, Shirt 2…).
function shirtFrom(text) {
  const match = String(text || '').match(/(shirt|tee|item|design|person)\s*#?\s*(\d+)/i);
  return match ? `${match[1].charAt(0).toUpperCase()}${match[1].slice(1).toLowerCase()} ${match[2]}` : '';
}

function labelFor(root) {
  let node = root;
  for (let depth = 0; node && depth < 5; depth += 1) {
    node = node.parentElement;
    const label = node?.querySelector(':scope > label, :scope > legend, :scope > [class*="label"], :scope > [class*="title"]');
    if (label && !label.contains(root) && !label.closest('.filepond--root')) {
      const text = label.textContent.replace(/[*:]/g, '').replace(/\s+/g, ' ').trim();
      if (text && text.length < 80) return text;
    }
  }
  return '';
}

function headingFor(root, gallery) {
  const names = [...root.querySelectorAll('input[name]')].map((input) => input.getAttribute('name') || '');
  const label = labelFor(root);
  let shirt = shirtFrom(label) || names.map(shirtFrom).find(Boolean) || '';
  if (!shirt) {
    const roots = [...document.querySelectorAll('.filepond--root')];
    if (roots.length > 1) shirt = fill(gallery.dataset.shirtTemplate, { number: roots.indexOf(root) + 1 });
  }
  return shirt ? fill(gallery.dataset.titleForTemplate, { name: shirt }) : gallery.dataset.title || '';
}

function findItemElement(root, id) {
  if (!id) return null;
  if (id.startsWith('cp-ref-')) return root.querySelector(`.filepond--item[data-cp-ref-id="${CSS.escape(id)}"]`);
  return root.querySelector(`#filepond--item-${CSS.escape(id)}`);
}

// Presses the app's own remove control so its saved data stays right. FilePond keeps several action buttons per
// photo and switches off the ones not in use, so only enabled ones are tried, one after another, until it's gone.
function removeItem(root, id) {
  root.dataset.cpRefChanged = 'true';
  const element = findItemElement(root, id);
  const selectors = [
    '.filepond--action-remove-item',
    '.filepond--action-revert-item-processing',
    '.filepond--action-abort-item-processing',
    '.filepond--action-abort-item-load',
  ];
  const controls = element
    ? selectors.flatMap((selector) => [...element.querySelectorAll(selector)]).filter((button) => !button.disabled && !button.hasAttribute('disabled'))
    : [];

  const pond = window.FilePond?.find?.(root);
  const attempt = (index) => {
    if (!element || !element.isConnected || element.getAttribute('data-filepond-item-state')?.includes('removed')) return schedule(root);
    if (index < controls.length) {
      controls[index].dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      setTimeout(() => attempt(index + 1), 350);
      return;
    }
    if (pond?.removeFile && !String(id).startsWith('cp-ref-')) pond.removeFile(id);
    schedule(root);
  };
  if (element) attempt(0);
  else if (pond?.removeFile && !String(id).startsWith('cp-ref-')) pond.removeFile(id);
  lockedUrls.delete(id);

  if (blobUrls.has(id)) {
    URL.revokeObjectURL(blobUrls.get(id));
    blobUrls.delete(id);
  }
  schedule(root);
  setTimeout(() => schedule(root), 500);
  setTimeout(() => schedule(root), 1500);
}

function ensureGallery(root) {
  const next = root.nextElementSibling;
  if (next?.matches('[data-ref-gallery-root]')) return next;
  const gallery = galleryTemplate.content.firstElementChild.cloneNode(true);
  root.insertAdjacentElement('afterend', gallery);
  return gallery;
}

function disarm(item) {
  clearTimeout(Number(item.dataset.confirmTimer || 0));
  item.removeAttribute('data-confirm');
}

// Upload copy for the empty uploader: "Add photos" (+ shirt number on packs), an "Optional" tag and a small helper
// line, placed in the app's wrapper just before its FilePond box. CSS turns the wrapper into the dashed button and
// stretches FilePond invisibly over it, so taps and drops still go to the app. Nothing inside FilePond is touched.
function refreshUploadCopy(root) {
  const wrapper = root.closest('.cl-upload--wrapper');
  if (!wrapper || root.parentElement !== wrapper) return;
  let box = wrapper.querySelector(':scope > .cp-drop');
  if (box && box.nextElementSibling === root) return;
  const copy = galleryTemplate.content.firstElementChild.dataset;
  const appLabel = wrapper.querySelector('.cl-upload--label');
  const shirt = (appLabel?.textContent.match(/(?:shirt|tee)\s*\d+/i)?.[0] || '').replace(/^\w/, (c) => c.toUpperCase());
  if (!box) {
    box = document.createElement('div');
    box.className = 'cp-drop';
    box.setAttribute('aria-hidden', 'true');
    const title = document.createElement('span');
    title.className = 'cp-drop-title';
    const heading = document.createElement('span');
    heading.textContent = copy.uploadHeading + (shirt ? ` · ${shirt}` : '');
    const optional = document.createElement('span');
    optional.className = 'cp-drop-optional';
    optional.textContent = copy.uploadOptional;
    title.append(heading, optional);
    const help = document.createElement('span');
    help.className = 'cp-drop-help';
    help.textContent = copy.uploadHelp;
    box.append(title, help);
  }
  root.before(box);
}

function render(root) {
  if (!root.isConnected) return;
  refreshUploadCopy(root);
  const items = readItems(root);
  const existing = root.nextElementSibling?.matches('[data-ref-gallery-root]') ? root.nextElementSibling : null;

  // Photos whose image can't be read yet still get a tile (photo icon, no file name) so the app's file-name
  // list never comes back; removing works the same, and the photo fills in once it's found.
  if (!items.length) {
    root.classList.remove('cp-ref-managed');
    existing?.remove();
    return;
  }

  const gallery = existing || ensureGallery(root);
  root.classList.add('cp-ref-managed');
  const list = gallery.querySelector('[data-ref-gallery-list]');
  const count = gallery.querySelector('[data-ref-gallery-count]');
  if (count) count.textContent = fill(items.length === 1 ? gallery.dataset.countOne : gallery.dataset.countOther, { count: items.length });
  const heading = gallery.querySelector('[data-ref-gallery-title]');
  const headingText = headingFor(root, gallery);
  if (heading && heading.textContent !== headingText) heading.textContent = headingText;

  const byId = new Map([...list.children].map((li) => [li.dataset.id, li]));
  const ordered = items.map((data, index) => {
    const li = byId.get(data.id) || itemTemplate.content.firstElementChild.cloneNode(true);
    li.dataset.id = data.id;
    li.dataset.state = data.state;
    // Cover background: the photo always fills the square tile, whatever its shape.
    const view = li.querySelector('[data-ref-view]');
    const placeholder = li.querySelector('[data-ref-placeholder]');
    if ((li.dataset.url || '') !== data.url) {
      li.dataset.url = data.url;
      view.style.backgroundImage = data.url ? `url("${data.url.replace(/"/g, '%22')}")` : '';
    }
    if (placeholder) placeholder.toggleAttribute('hidden', Boolean(li.dataset.url));
    li.querySelector('[data-ref-view]').setAttribute('aria-label', fill(gallery.dataset.viewTemplate, { number: index + 1 }));
    li.querySelector('[data-ref-remove]').setAttribute('aria-label', fill(gallery.dataset.removeTemplate, { number: index + 1 }));
    return li;
  });
  list.replaceChildren(...ordered);
}

function schedule(root) {
  if (root) pending.add(root);
  else document.querySelectorAll('.filepond--root').forEach((el) => pending.add(el));
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const roots = [...pending];
    pending.clear();
    roots.forEach(render);
  });
}

if (galleryTemplate && itemTemplate) {
  // Grid controls.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const item = target?.closest('[data-ref-item]');
    const gallery = item?.closest('[data-ref-gallery-root]');
    const root = gallery?.previousElementSibling;
    if (!item || !gallery || !root?.matches('.filepond--root')) return;

    if (target.closest('[data-ref-view]')) {
      event.preventDefault();
      const items = [...gallery.querySelectorAll('[data-ref-item]')];
      const urls = items.map((li) => li.dataset.url || '');
      const withPhotos = urls.filter(Boolean);
      const start = withPhotos.indexOf(urls[items.indexOf(item)]);
      if (withPhotos.length && typeof window.cpOpenImageViewer === 'function') {
        window.cpOpenImageViewer(withPhotos, Math.max(0, start), headingFor(root, gallery));
      }
      return;
    }

    if (target.closest('[data-ref-remove]')) {
      event.preventDefault();
      gallery.querySelectorAll('[data-ref-item][data-confirm]').forEach(disarm);
      item.setAttribute('data-confirm', '');
      item.dataset.confirmTimer = String(setTimeout(() => disarm(item), CONFIRM_TIMEOUT));
      item.querySelector('[data-ref-remove-cancel]')?.focus();
      return;
    }

    if (target.closest('[data-ref-remove-cancel]')) {
      event.preventDefault();
      disarm(item);
      item.querySelector('[data-ref-remove]')?.focus();
      return;
    }

    if (target.closest('[data-ref-remove-confirm]')) {
      event.preventDefault();
      disarm(item);
      removeItem(root, item.dataset.id);
    }
  });

  // FilePond announces every change with bubbling "FilePond:*" events on its root.
  ['addfile', 'removefile', 'processfile', 'processfilestart', 'processfileabort', 'processfilerevert', 'updatefiles', 'init', 'error', 'preparefile']
    .forEach((name) => document.addEventListener(`FilePond:${name}`, (event) => {
      const root = event.target instanceof Element ? event.target.closest('.filepond--root') : null;
      schedule(root || undefined);
      // Previews and uploads finish a moment after the event; check again shortly after.
      setTimeout(() => schedule(root || undefined), 600);
      setTimeout(() => schedule(root || undefined), 1800);
    }));

  // Fallback for app builds that don't emit events (and for uploaders added later, e.g. switching packs).
  new MutationObserver((records) => {
    for (const record of records) {
      const element = record.target instanceof Element ? record.target : null;
      const root = element?.closest('.filepond--root');
      if (root) schedule(root);
      else if (element && [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element && (node.matches('.filepond--root') || node.querySelector?.('.filepond--root')))) schedule();
    }
    // Grids whose uploader was removed go too.
    document.querySelectorAll('[data-ref-gallery-root]').forEach((gallery) => {
      if (!gallery.previousElementSibling?.matches('.filepond--root')) gallery.remove();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-filepond-item-state'] });

  schedule();
  window.addEventListener('load', () => schedule());
}

// Edit from the cart lands on the product options, not the top of the page. s-cart-addon.js stores cp:editing
// (line key, product path, time) when Edit is tapped; on arrival we wait for the options app to draw its fields,
// then scroll to the first one, leaving room for a sticky header. Runs once per Edit tap.
(() => {
  let editing = null;
  try { editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null'); } catch {}
  if (!editing || editing.scrolled || Date.now() - Number(editing.t || 0) > 2 * 60 * 1000) return;
  if (editing.path && !location.pathname.endsWith(editing.path) && !editing.path.endsWith(location.pathname)) return;

  const OPTIONS = '[class^="cl-po"], [class*=" cl-po"]';
  const findOptions = () => document.querySelector(OPTIONS) || document.querySelector('.filepond--root') || document.querySelector('[data-product-form]');

  const headerOffset = () => {
    const header = document.querySelector('header');
    if (!header) return 0;
    const style = getComputedStyle(header);
    return style.position === 'sticky' || style.position === 'fixed' ? header.getBoundingClientRect().height : 0;
  };

  const scrollToOptions = (target) => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    try { sessionStorage.setItem('cp:editing', JSON.stringify({ ...editing, scrolled: true })); } catch {}
  };

  // The options app renders after the page loads: wait for its fields (up to 8s), then fall back to the product form.
  const ready = document.querySelector(OPTIONS);
  if (ready) return void requestAnimationFrame(() => scrollToOptions(ready));
  const observer = new MutationObserver(() => {
    const found = document.querySelector(OPTIONS);
    if (!found) return;
    observer.disconnect();
    clearTimeout(fallback);
    // Give the app a moment to finish laying out so the scroll lands in the right place.
    setTimeout(() => scrollToOptions(found), 250);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  const fallback = setTimeout(() => {
    observer.disconnect();
    const target = findOptions();
    if (target) scrollToOptions(target);
  }, 8000);
})();
